import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaTrash,
  FaPlus,
  FaSignInAlt,
  FaSignOutAlt,
  FaChevronDown,
  FaChevronRight,
  FaFileExcel,
  FaFileArchive,
  FaEdit,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaRegClock,
  FaBan,
  FaUsers,
  FaBoxOpen,
  FaTools,
  FaCamera,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaArrowRight,
} from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import { SelectWithSearch, SelectOption } from "../../../utils/select-with-search";
import IncFilesPanel from "../components/IncFilesPanel";
import FieldWorkPanel from "../components/FieldWorkPanel";
import VerificationMediaPanel, {
  VerificationStatusBadge,
  type VisitVerification,
} from "../components/VerificationMediaPanel";
import VisitCheckInModal from "../components/VisitCheckInModal";
import UnitSelect from "../components/UnitSelect";
import { downloadIncFile } from "../inc_downloads";

const SOURCE_OPTIONS = [
  { value: "espl_warehouse", label: "ESPL Warehouse" },
  { value: "staff_inventory", label: "Staff Inventory" },
  { value: "client_material", label: "Client Material" },
  { value: "direct_purchase", label: "Direct Purchase" },
];

// Default source for a new installed-material line. No source is quantity
// capped any more — the API records material as reported — but for warehouse
// material the delivered / installed figures are still shown for reference.
const STOCK_CONTROLLED_SOURCE = "espl_warehouse";

const STATUS_LABEL: Record<string, string> = {
  planned: "Planned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const statusBadge = (s: string) => {
  switch (s) {
    case "completed": return "bg-green-100 text-green-800";
    case "in_progress": return "bg-blue-100 text-blue-800";
    case "cancelled": return "bg-red-100 text-red-800";
    default: return "bg-gray-100 text-gray-700";
  }
};

const statusLabel = (s?: string) => STATUS_LABEL[s || ""] || (s || "-").replace(/_/g, " ");

// Visits carry real timestamps, so date-only formatting was hiding when work
// actually happened. Dates print as dates, moments print as date + time.
const dateText = (v?: string) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};
const dateTimeText = (v?: string) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};
const timeText = (v?: string) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "-" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

// Time on site, from check-in to check-out.
const durationText = (from?: string, to?: string) => {
  if (!from || !to) return "-";
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "-";
  const mins = Math.round(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const personName = (u?: any, fallbackId?: number) =>
  u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || `#${fallbackId ?? ""}` : fallbackId ? `#${fallbackId}` : "-";

const initialsOf = (u?: any) => {
  const f = u?.first_name?.[0] || "";
  const l = u?.last_name?.[0] || "";
  return (f + l || u?.username?.slice(0, 2) || "?").toUpperCase();
};

// The geo stamp on each end of a visit. Both halves are shown, not just the
// address the engineer typed — the address is what they said, the coordinates
// are where the phone actually was, and reconciling the two is the whole reason
// check-in is geo-stamped.
const coordText = (lat?: number | string, lng?: number | string) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return `${a.toFixed(6)}, ${b.toFixed(6)}`;
};

// Coordinates, with a link out to the map. Falls back to a plain dash when the
// visit has not reached that end of its lifecycle yet.
const GeoCell: React.FC<{ label: string; lat?: number | string; lng?: number | string }> = ({ label, lat, lng }) => {
  const text = coordText(lat, lng);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      {text ? (
        <a
          href={`https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`}
          target="_blank"
          rel="noreferrer"
          title="Open in Google Maps"
          className="inline-flex items-center gap-1 text-sm font-mono text-blue-700 hover:underline break-all"
        >
          <FaMapMarkerAlt className="w-3 h-3 flex-shrink-0" />
          {text}
        </a>
      ) : (
        <div className="text-sm text-gray-400">-</div>
      )}
    </div>
  );
};

const InfoCell: React.FC<{ label: string; value: React.ReactNode; wide?: boolean }> = ({ label, value, wide }) => (
  <div className={wide ? "col-span-2 md:col-span-3 lg:col-span-4" : undefined}>
    <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className="text-sm text-gray-800 break-words">{value}</div>
  </div>
);

// One step of the Planned → Checked in → Checked out strip on a visit header.
const LifecycleStep: React.FC<{ done: boolean; active?: boolean; label: string; sub?: string }> = ({
  done,
  active,
  label,
  sub,
}) => (
  <div className="flex items-center gap-1.5">
    <span
      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
        done ? "bg-green-500 text-white" : active ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-400"
      }`}
    >
      {done ? <FaCheckCircle className="w-2.5 h-2.5" /> : <FaRegClock className="w-2.5 h-2.5" />}
    </span>
    <span className={`text-xs ${done || active ? "text-gray-700" : "text-gray-400"}`}>
      {label}
      {sub && sub !== "-" && <span className="text-gray-400"> · {sub}</span>}
    </span>
  </div>
);

// Every button inside a visit card carries its own permission, so a field
// engineer can record work without being able to sign a phase off.
interface VisitPermissions {
  checkIn: boolean;
  checkOut: boolean;
  material: boolean;
  equipment: boolean;
  files: boolean;
  members: boolean;
  edit: boolean;
  export: boolean;
  downloadFiles: boolean;
  // Manual cancel of an open visit — any visit, not only one's own.
  cancel: boolean;
  // Approve / reject the check-in verification step. Never one's own visit.
  verify: boolean;
}

type VisitSection = "details" | "team" | "material" | "equipment" | "files";

interface VisitCardProps {
  visit: any;
  siteId: string;
  // True only for the engineer who created this visit — check in/out, equipment
  // and file uploads belong to them.
  isOwner: boolean;
  currentUserId?: number;
  perms: VisitPermissions;
  open: boolean;
  onToggle: () => void;
  onChange: () => void;
}

const VisitCard: React.FC<VisitCardProps> = ({ visit, siteId, isOwner, currentUserId, perms, open, onToggle, onChange }) => {
  const { prompt, confirm } = useConfirm();
  const [section, setSection] = useState<VisitSection>("details");
  // The picker always starts empty — nothing is pre-selected, and the API never
  // offers the visit's own engineer as a candidate.
  const [member, setMember] = useState<SelectOption | null>(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  // The visits list embeds a bare {id, user_id, user} for each member; the
  // dedicated endpoint adds designation, role and the is_supervisor flag, so the
  // card starts from the embedded rows and upgrades them once it is expanded.
  const [members, setMembers] = useState<any[]>(visit.members || []);
  const [membersLoading, setMembersLoading] = useState(false);
  const [matItem, setMatItem] = useState<SelectOption | null>(null);
  const [matQty, setMatQty] = useState("");
  const [matUnit, setMatUnit] = useState("");
  const [matRemarks, setMatRemarks] = useState("");
  const [matSource, setMatSource] = useState(STOCK_CONTROLLED_SOURCE);
  const [matSaving, setMatSaving] = useState(false);
  const [availability, setAvailability] = useState<any>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [eqName, setEqName] = useState("");
  const [eqQty, setEqQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    visit_date: "",
    address: "",
    work_done: "",
    next_day_plan: "",
    remarks: "",
  });

  const checkedIn = !!visit.check_in_time;
  const checkedOut = !!visit.check_out_time;
  const isCancelled = visit.visit_status === "cancelled";
  // Check-in verification. `required` is the project's switch; when it is on
  // the visit cannot be checked in until both parts are approved.
  const verification: VisitVerification | undefined = visit.verification;
  const verificationRequired = !!verification?.required;
  const verificationBlocksCheckIn = verificationRequired && !checkedIn && !verification?.can_check_in;
  const materials: any[] = visit.materials || [];
  const equipment: any[] = visit.equipment || [];

  useEffect(() => {
    setMembers(visit.members || []);
  }, [visit.members]);

  // GET /inc/visits/:id/members — the authoritative list, refreshed after every
  // add or remove so the chips update without reloading every visit on the site.
  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const res = await api.get(`/inc/visits/${visit.id}/members`);
      setMembers(res.data?.data?.members || []);
    } catch {
      /* keep whatever the visits list gave us */
    } finally {
      setMembersLoading(false);
    }
  }, [visit.id]);

  useEffect(() => {
    if (open) loadMembers();
  }, [open, loadMembers]);

  // Delivered / installed figures for the selected item on this site. Shown for
  // reference only — nothing is capped.
  useEffect(() => {
    if (!matItem) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    api
      .get(`/inc/sites/${siteId}/material-availability`, { params: { item_id: matItem.id } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        setAvailability(data || null);
        // Pre-fill the unit from the item master rather than asking for it again.
        const unit = data?.item?.unit;
        if (unit) setMatUnit(unit.short_name || unit.name || "");
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matItem, siteId]);

  const stockControlled = matSource === STOCK_CONTROLLED_SOURCE;
  const availableQty = Number(availability?.available_quantity ?? 0);
  const overAvailable = stockControlled && !!availability && Number(matQty) > availableQty;
  const isOpenVisit = visit.visit_status === "planned" || visit.visit_status === "in_progress";

  const cancelVisit = async () => {
    const reason = await prompt({
      title: `Cancel visit #${visit.visit_number}`,
      message:
        "The visit is closed as cancelled whoever created it. It frees the engineer to start another visit and no longer blocks completing or cancelling the field work.",
      label: "Reason for cancelling",
      placeholder: "Why is this visit being cancelled?",
      required: true,
      multiline: true,
      maxLength: 2000,
      confirmText: "Cancel visit",
      cancelText: "Keep it",
      variant: "danger",
    });
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required to cancel a visit");
    setBusy(true);
    try {
      await api.post(`/inc/visits/${visit.id}/cancel`, { cancel_reason: reason.trim() });
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  const geo = (cb: (lat?: number, lng?: number) => void) => {
    if (!navigator.geolocation) return cb();
    navigator.geolocation.getCurrentPosition(
      (pos) => cb(pos.coords.latitude, pos.coords.longitude),
      () => cb(),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  // Check-in opens a dialog rather than posting straight away, because the
  // verification snaps and clip are captured as part of it and land in the same
  // request. The dialog owns the geolocation call from here on.
  const checkIn = () => {
    if (checkedIn) return;
    setCheckInOpen(true);
  };

  // Guarded on the client as well as the server: a visit that was never checked
  // in has no start time to check out against.
  const checkOut = async () => {
    if (!checkedIn) {
      toast.error("Check in first — a visit cannot be checked out before it is started");
      return;
    }
    if (checkedOut) return;
    const ok = await confirm({
      title: `Check out of visit #${visit.visit_number}?`,
      message: "Checking out ends this visit and stamps the time and your location. Make sure material, equipment and photos are recorded first.",
      confirmText: "Check out",
      variant: "info",
    });
    if (!ok) return;
    setBusy(true);
    geo(async (lat, lng) => {
      try {
        await api.post(`/inc/visits/${visit.id}/check-out`, { latitude: lat, longitude: lng });
        onChange();
      } catch {
        /* the axios interceptor already surfaced the message */
      } finally {
        setBusy(false);
      }
    });
  };

  const startEdit = () => {
    setEditForm({
      // <input type="date"> only accepts YYYY-MM-DD.
      visit_date: visit.visit_date ? new Date(visit.visit_date).toISOString().slice(0, 10) : "",
      address: visit.address || "",
      work_done: visit.work_done || "",
      next_day_plan: visit.next_day_plan || "",
      remarks: visit.remarks || "",
    });
    setSection("details");
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/inc/visits/${visit.id}`, {
        visit_date: editForm.visit_date || undefined,
        address: editForm.address,
        work_done: editForm.work_done,
        next_day_plan: editForm.next_day_plan,
        remarks: editForm.remarks,
      });
      setEditing(false);
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    if (!member) return toast.error("Select a team member");
    try {
      await api.post(`/inc/visits/${visit.id}/members`, { user_id: member.id });
      setMember(null);
      await loadMembers();
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const removeMember = async (m: any) => {
    const ok = await confirm({
      title: "Remove member?",
      message: `${personName(m.user, m.user_id)} will no longer be recorded on visit #${visit.visit_number}.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/visit-members/${m.id}`);
      await loadMembers();
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const addMaterial = async () => {
    if (!matItem || !matQty) return toast.error("Item and quantity required");
    if (!(Number(matQty) > 0)) return toast.error("Quantity must be greater than 0");
    setMatSaving(true);
    try {
      await api.post(`/inc/visits/${visit.id}/materials`, {
        inventory_item_id: matItem.id,
        quantity: Number(matQty),
        unit: matUnit || undefined,
        remarks: matRemarks || undefined,
        source_type: matSource,
      });
      setMatItem(null);
      setMatQty("");
      setMatUnit("");
      setMatRemarks("");
      setAvailability(null);
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setMatSaving(false);
    }
  };

  const removeMaterial = async (m: any) => {
    const ok = await confirm({
      title: "Remove installed material?",
      message: `${m.item ? `${m.item.item_code} — ${m.item.item_name}` : "This line"} (${m.quantity}${m.unit ? ` ${m.unit}` : ""}) will be removed from visit #${visit.visit_number}.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/visit-materials/${m.id}`);
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const addEquipment = async () => {
    if (!eqName) return toast.error("Equipment name required");
    try {
      await api.post(`/inc/visits/${visit.id}/equipment`, { equipment_name: eqName, quantity: Number(eqQty) || 1 });
      setEqName("");
      setEqQty("");
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const removeEquipment = async (e: any) => {
    const ok = await confirm({
      title: "Remove equipment?",
      message: `${e.equipment_name} will be removed from visit #${visit.visit_number}.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/visit-equipment/${e.id}`);
      onChange();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  // A cancelled visit is closed: nothing left to start or end.
  const canCheckIn = isOwner && perms.checkIn && !isCancelled;
  const canCheckOut = isOwner && perms.checkOut && !isCancelled;
  const canCancelVisit = perms.cancel && isOpenVisit;
  const canEditVisit = isOwner && perms.edit;
  const canRecordMaterials = perms.material;
  const canRecordEquipment = isOwner && perms.equipment;
  const canManageFiles = isOwner && perms.files;

  const sections: Array<{ key: VisitSection; label: string; icon: React.ComponentType<{ className?: string }>; count?: number }> = [
    { key: "details", label: "Details", icon: FaInfoCircle },
    { key: "team", label: "Team", icon: FaUsers, count: members.length },
    { key: "material", label: "Material", icon: FaBoxOpen, count: materials.length },
    { key: "equipment", label: "Equipment", icon: FaTools, count: equipment.length },
    { key: "files", label: "Photos & Files", icon: FaCamera },
  ];

  // What happens next on this visit, in one line, with the button that does it.
  const nextStep = (() => {
    if (isCancelled) {
      return {
        tone: "border-red-200 bg-red-50 text-red-800",
        icon: FaBan,
        text: `Cancelled ${dateTimeText(visit.cancelled_at)}${visit.cancel_reason ? ` — ${visit.cancel_reason}` : ""}`,
        action: null as React.ReactNode,
      };
    }
    if (checkedOut) {
      return {
        tone: "border-green-200 bg-green-50 text-green-800",
        icon: FaCheckCircle,
        text: `Completed · ${dateTimeText(visit.check_in_time)} → ${dateTimeText(visit.check_out_time)} (${durationText(visit.check_in_time, visit.check_out_time)} on site)`,
        action: null as React.ReactNode,
      };
    }
    if (checkedIn) {
      return {
        tone: "border-blue-200 bg-blue-50 text-blue-900",
        icon: FaRegClock,
        text: isOwner
          ? `On site since ${dateTimeText(visit.check_in_time)}. Record material, equipment and photos, then check out to finish the visit.`
          : `On site since ${dateTimeText(visit.check_in_time)} — only the engineer who created this visit can check out.`,
        action: canCheckOut ? (
          <button
            onClick={checkOut}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-60 flex-shrink-0"
          >
            <FaSignOutAlt className="w-3 h-3" /> Check Out
          </button>
        ) : null,
      };
    }
    if (verificationBlocksCheckIn) {
      const rejected = verification?.status === "rejected";
      return {
        tone: rejected ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-900",
        icon: rejected ? FaBan : FaRegClock,
        text: isOwner
          ? rejected
            ? "Verification was rejected. Submit it again under Photos & Files — check-in opens once both parts are approved."
            : "Check-in is locked until the visit verification and the safety verification are approved. Submit them under Photos & Files — that sends them for approval."
          : perms.verify
            ? "Waiting for verification approval before the engineer can check in. Review it under Photos & Files."
            : "Waiting for verification approval before the engineer can check in.",
        action: (
          <button
            onClick={() => setSection("files")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-blue-800 border border-blue-200 bg-white rounded hover:bg-blue-50 flex-shrink-0"
          >
            <FaCamera className="w-3 h-3" /> {perms.verify && !isOwner ? "Review verification" : "Open Photos & Files"}
          </button>
        ) as React.ReactNode,
      };
    }
    return {
      tone: "border-amber-200 bg-amber-50 text-amber-900",
      icon: FaRegClock,
      text: isOwner
        ? verificationRequired
          ? "Verification is approved. Check in when you are on site — your location is captured."
          : "Not started yet. Submit the verification photos under Photos & Files, then check in when you are on site — check-in records your location only."
        : "Not started yet — only the engineer who created this visit can check in.",
      action: canCheckIn ? (
        <button
          onClick={checkIn}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60 flex-shrink-0"
        >
          <FaSignInAlt className="w-3 h-3" /> Check In
        </button>
      ) : null,
    };
  })();
  const NextIcon = nextStep.icon;

  const engineer = visit.creator;

  return (
    <div
      id={`inc-visit-${visit.id}`}
      className={`border rounded-lg bg-white transition-shadow ${open ? "border-blue-200 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
    >
      {/* Header — everything needed to scan the list without opening a card. */}
      <button onClick={onToggle} className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
          {open ? <FaChevronDown className="w-3 h-3 text-gray-400" /> : <FaChevronRight className="w-3 h-3 text-gray-400" />}
          <span className="font-semibold text-gray-800">Visit #{visit.visit_number}</span>
          <span className={`px-2 py-0.5 text-xs rounded-full ${statusBadge(visit.visit_status)}`}>{statusLabel(visit.visit_status)}</span>
          {isOwner && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-800 text-white tracking-wide">MY VISIT</span>
          )}
          {/* Only while it still matters: before check-in, on a project that requires
              it or once something was submitted for review on any project. */}
          {!checkedIn && !isCancelled && verification && (verificationRequired || verification.status !== "not_required") && (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
              Verification <VerificationStatusBadge status={verification.status} />
              {perms.verify && !isOwner && verification.status === "pending" && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!open) onToggle();
                    setSection("files");
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.stopPropagation();
                    if (!open) onToggle();
                    setSection("files");
                  }}
                  className="text-blue-700 hover:underline font-medium"
                >
                  Review
                </span>
              )}
            </span>
          )}
          {!isCancelled && (
            <div className="flex flex-wrap items-center gap-2">
              <LifecycleStep done label="Planned" sub={dateText(visit.visit_date)} />
              <span className="w-4 h-px bg-gray-300" />
              <LifecycleStep done={checkedIn} active={!checkedIn} label="Checked in" sub={timeText(visit.check_in_time)} />
              <span className="w-4 h-px bg-gray-300" />
              <LifecycleStep done={checkedOut} active={checkedIn && !checkedOut} label="Checked out" sub={timeText(visit.check_out_time)} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold flex items-center justify-center">
              {initialsOf(engineer)}
            </span>
            {personName(engineer, visit.created_by)}
          </span>
          <span className="inline-flex items-center gap-1" title="Members"><FaUsers className="w-3 h-3" /> {members.length}</span>
          <span className="inline-flex items-center gap-1" title="Installed material lines"><FaBoxOpen className="w-3 h-3" /> {materials.length}</span>
          <span className="inline-flex items-center gap-1" title="Installed equipment"><FaTools className="w-3 h-3" /> {equipment.length}</span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
          {/* Next step + card actions */}
          <div className={`flex flex-wrap items-center justify-between gap-3 rounded border px-3 py-2 ${nextStep.tone}`}>
            <div className="flex items-start gap-2 text-sm min-w-0">
              <NextIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{nextStep.text}</span>
            </div>
            {nextStep.action}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Section switcher — one part of the visit at a time. */}
            <div className="flex flex-wrap gap-1 border-b border-gray-200 w-full sm:w-auto">
              {sections.map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => setSection(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    section === key ? "border-blue-800 text-blue-800" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                  {count !== undefined && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${section === key ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}>
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {canEditVisit && !editing && (
                <button onClick={startEdit} title="Edit visit details" className="p-2 text-blue-700 hover:bg-blue-50 rounded">
                  <FaEdit className="w-4 h-4" />
                </button>
              )}
              {perms.export && (
                <button
                  onClick={() =>
                    downloadIncFile(`/inc/sites/${siteId}/visits/export`, `inc-visit-${visit.visit_number}.xlsx`, { visit_id: visit.id })
                  }
                  title="Export this visit to Excel"
                  className="p-2 text-green-700 hover:bg-green-50 rounded"
                >
                  <FaFileExcel className="w-4 h-4" />
                </button>
              )}
              {perms.downloadFiles && (
                <button
                  onClick={() =>
                    downloadIncFile(`/inc/sites/${siteId}/visits/download-files`, `inc-visit-${visit.visit_number}-files.zip`, {
                      visit_id: visit.id,
                    })
                  }
                  title="Download this visit's files (ZIP)"
                  className="p-2 text-indigo-700 hover:bg-indigo-50 rounded"
                >
                  <FaFileArchive className="w-4 h-4" />
                </button>
              )}
              {canCancelVisit && (
                <button onClick={cancelVisit} disabled={busy} title="Cancel this visit" className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-60">
                  <FaBan className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* ── Details ─────────────────────────────────────────────────── */}
          {section === "details" &&
            (editing ? (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Planned date</label>
                    <input
                      type="date"
                      value={editForm.visit_date}
                      onChange={(e) => setEditForm({ ...editForm, visit_date: e.target.value })}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Address</label>
                    <input
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Work done</label>
                    <textarea
                      rows={2}
                      value={editForm.work_done}
                      onChange={(e) => setEditForm({ ...editForm, work_done: e.target.value })}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Next day plan</label>
                    <textarea
                      rows={2}
                      value={editForm.next_day_plan}
                      onChange={(e) => setEditForm({ ...editForm, next_day_plan: e.target.value })}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Remarks</label>
                    <input
                      value={editForm.remarks}
                      onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400">Check-in and check-out times are not editable — they are stamped by those buttons.</p>
                <div className="flex gap-2">
                  <button
                    onClick={saveEdit}
                    disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-60"
                  >
                    <FaSave className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    <FaTimes className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-gray-50 border border-gray-200 rounded p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <InfoCell label="Planned Date" value={dateText(visit.visit_date)} />
                  <InfoCell label="Created On" value={dateTimeText(visit.created_at)} />
                  <InfoCell label="Engineer" value={personName(engineer, visit.created_by)} />
                  <InfoCell label="Time On Site" value={durationText(visit.check_in_time, visit.check_out_time)} />
                  <InfoCell label="Check In" value={dateTimeText(visit.check_in_time)} />
                  <GeoCell label="Check In Location" lat={visit.check_in_latitude} lng={visit.check_in_longitude} />
                  <InfoCell label="Check Out" value={dateTimeText(visit.check_out_time)} />
                  <GeoCell label="Check Out Location" lat={visit.check_out_latitude} lng={visit.check_out_longitude} />
                  <InfoCell label="Address" value={visit.address || "-"} wide />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { label: "Work done", value: visit.work_done },
                    { label: "Next day plan", value: visit.next_day_plan },
                    { label: "Remarks", value: visit.remarks },
                  ].map(({ label, value }) => (
                    <div key={label} className="border border-gray-200 rounded p-3">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{label}</div>
                      <p className={`text-sm whitespace-pre-wrap ${value ? "text-gray-800" : "text-gray-400"}`}>{value || "Not recorded"}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {/* ── Team ────────────────────────────────────────────────────── */}
          {/* The site's assigned team only. A visit starts with just the engineer
              who created it; they, this site's supervisor and IncSite.assign
              holders may add the rest. The SUPERVISOR badge is only ever the
              site's supervisor from the Team tab. Nobody can remove the visit's
              creator, and nobody can remove themselves. */}
          {section === "team" && (
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {members.map((m: any) => {
                  const sup = !!m.is_supervisor;
                  const isCreator = !!m.is_visit_creator || Number(m.user_id) === Number(visit.created_by);
                  const isSelf = currentUserId != null && Number(m.user_id) === Number(currentUserId);
                  return (
                    <span
                      key={m.id}
                      className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-1 text-xs rounded-full border ${
                        sup ? "bg-blue-50 text-blue-900 border-blue-100" : "bg-gray-50 text-gray-800 border-gray-200"
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full bg-white text-[9px] font-bold flex items-center justify-center border border-gray-200">
                        {initialsOf(m.user)}
                      </span>
                      {personName(m.user, m.user_id)}
                      {m.user?.designation?.name && <span className={sup ? "text-blue-400" : "text-gray-400"}>· {m.user.designation.name}</span>}
                      {sup && <span className="px-1 text-[10px] font-semibold text-blue-800 bg-blue-100 rounded">SUPERVISOR</span>}
                      {isCreator && (
                        <span className="px-1 text-[10px] font-semibold text-gray-600 bg-gray-200 rounded">
                          {isSelf ? "YOU · ENGINEER" : "ENGINEER"}
                        </span>
                      )}
                      {perms.members && !isCreator && !isSelf && (
                        <button onClick={() => removeMember(m)} className="ml-0.5 text-red-500 hover:text-red-700" title="Remove member">
                          <FaTimes className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </span>
                  );
                })}
                {members.length === 0 && <span className="text-xs text-gray-400">{membersLoading ? "Loading…" : "No members recorded."}</span>}
              </div>
              {perms.members ? (
                <div className="max-w-md">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <SelectWithSearch
                        value={member}
                        onChange={setMember}
                        apiEndpoint={`/inc/visits/${visit.id}/assignable-members`}
                        dataKey="users"
                        labelKey="first_name"
                        displayFormat={(u) =>
                          `${`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username}${u.is_site_supervisor ? " — Site Supervisor" : ""}`
                        }
                        placeholder="Add member from site team..."
                        minSearchLength={0}
                      />
                    </div>
                    <button onClick={addMember} className="px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900">Add</button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Only users assigned to this site (Team tab) can be added. The visit's engineer is already on it.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Members are managed by the visit's engineer, the site supervisor or anyone who can assign the site team.</p>
              )}
            </div>
          )}

          {/* ── Installed material ──────────────────────────────────────── */}
          {/* BOQ-style line items, one set per visit. Not owner-only: anyone with
              the permission may add or correct a line. */}
          {section === "material" && (
            <div>
              {materials.length === 0 ? (
                <p className="text-sm text-gray-400 mb-3">No material recorded on this visit.</p>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded mb-3">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-left px-3 py-2">Unit</th>
                        <th className="text-left px-3 py-2">Source</th>
                        <th className="text-left px-3 py-2">Remarks</th>
                        {canRecordMaterials && <th className="px-3 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {materials.map((m: any) => (
                        <tr key={m.id}>
                          <td className="px-3 py-2">{m.item ? `${m.item.item_code} — ${m.item.item_name}` : `#${m.inventory_item_id}`}</td>
                          <td className="px-3 py-2 text-right">{m.quantity}</td>
                          <td className="px-3 py-2 text-gray-600">{m.unit || "-"}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{SOURCE_OPTIONS.find((o) => o.value === m.source_type)?.label || m.source_type}</td>
                          <td className="px-3 py-2 text-gray-600 break-words">{m.remarks || "-"}</td>
                          {canRecordMaterials && (
                            <td className="px-3 py-2 text-right">
                              <button onClick={() => removeMaterial(m)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove line">
                                <FaTrash className="w-3 h-3" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-200 font-medium bg-gray-50">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2 text-right">{materials.reduce((sum: number, m: any) => sum + Number(m.quantity || 0), 0)}</td>
                        <td colSpan={canRecordMaterials ? 4 : 3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {canRecordMaterials && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-600">Record installed material</div>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="min-w-[200px] flex-1">
                      <SelectWithSearch value={matItem} onChange={setMatItem} apiEndpoint="/inc/material-items" dataKey="items" labelKey="item_name" displayFormat={(i) => `${i.item_code} — ${i.item_name}`} placeholder="Material item..." minSearchLength={0} />
                    </div>
                    <input
                      type="number"
                      min={0}
                      placeholder="Qty"
                      value={matQty}
                      onChange={(e) => setMatQty(e.target.value)}
                      className="w-24 px-2 py-2 text-sm border border-gray-300 rounded bg-white"
                    />
                    <UnitSelect value={matUnit} onChange={setMatUnit} className="w-32" placeholder="Unit..." />
                    <select value={matSource} onChange={(e) => setMatSource(e.target.value)} className="px-2 py-2 text-sm border border-gray-300 rounded bg-white">
                      {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <input placeholder="Remarks" value={matRemarks} onChange={(e) => setMatRemarks(e.target.value)} className="min-w-[160px] flex-1 px-2 py-2 text-sm border border-gray-300 rounded bg-white" />
                    <button
                      onClick={addMaterial}
                      disabled={matSaving}
                      className="px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {matSaving ? "Saving..." : "Install"}
                    </button>
                  </div>
                  {/* Reference figures for the selected item — not a limit. */}
                  {matItem && stockControlled && (
                    <p className="text-xs text-gray-500">
                      {availabilityLoading
                        ? "Checking availability..."
                        : availability
                          ? availability.basis === "site_delivery"
                            ? `For reference: ${availability.delivered_quantity} delivered to this site, ${availability.installed_quantity} already installed (${availableQty} remaining)`
                            : `For reference: ${availableQty} in warehouse stock — nothing delivered to this site for this item yet`
                          : "Availability unavailable"}
                      {overAvailable ? " — more than that is being recorded, which is allowed." : ""}
                    </p>
                  )}
                  {matItem && !stockControlled && (
                    <p className="text-xs text-gray-500">{SOURCE_OPTIONS.find((o) => o.value === matSource)?.label} is not ESPL stock.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Installed equipment ─────────────────────────────────────── */}
          {section === "equipment" && (
            <div>
              {equipment.length === 0 ? (
                <p className="text-sm text-gray-400 mb-3">No equipment recorded on this visit.</p>
              ) : (
                <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded mb-3">
                  {equipment.map((e: any) => (
                    <li key={e.id} className="px-3 py-2 flex justify-between items-center">
                      <span>
                        <span className="font-medium text-gray-800">{e.equipment_name}</span>
                        {e.serial_number ? <span className="text-gray-500"> · S/N {e.serial_number}</span> : ""}
                        <span className="text-gray-500"> · Qty {e.quantity}</span>
                      </span>
                      {canRecordEquipment && (
                        <button onClick={() => removeEquipment(e)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove">
                          <FaTrash className="w-3 h-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canRecordEquipment ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 flex flex-wrap gap-2 items-end">
                  <input placeholder="Equipment name" value={eqName} onChange={(e) => setEqName(e.target.value)} className="flex-1 min-w-[180px] px-2 py-2 text-sm border border-gray-300 rounded bg-white" />
                  <input placeholder="Qty" value={eqQty} onChange={(e) => setEqQty(e.target.value)} className="w-24 px-2 py-2 text-sm border border-gray-300 rounded bg-white" />
                  <button onClick={addEquipment} className="px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900">Add</button>
                </div>
              ) : (
                !isOwner && <p className="text-xs text-gray-400">Only the visit's engineer records equipment.</p>
              )}
            </div>
          )}

          {/* ── Photos & files ──────────────────────────────────────────── */}
          {/* Check-in media (the visit verification photo plus the safety photos
              and videos) is its own panel above the work files: they are
              different records and are read separately. */}
          {section === "files" && (
            <div className="space-y-4">
              <VerificationMediaPanel
                visitId={visit.id}
                visitNumber={visit.visit_number}
                canManage={canManageFiles}
                checkedIn={checkedIn}
                visitStatus={visit.visit_status}
                isOwner={isOwner}
                canReview={perms.verify}
                canSubmit={perms.checkIn}
                onVerificationChange={onChange}
              />
              <IncFilesPanel
                filesUrl={`/inc/visits/${visit.id}/files`}
                deleteUrl={(fileId) => `/inc/visit-files/${fileId}`}
                folderName="inc/visit-files"
                canManage={canManageFiles}
                title="Visit files"
                emptyLabel="No files uploaded."
              />
              {!isOwner && <p className="text-xs text-gray-400">Only the visit's engineer uploads photos and files.</p>}
            </div>
          )}
        </div>
      )}

      <VisitCheckInModal
        visitId={visit.id}
        visitNumber={visit.visit_number}
        verificationRequired={verificationRequired}
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        onDone={onChange}
      />
    </div>
  );
};

const OPEN_STATUSES = ["planned", "in_progress"];

type StatusFilter = "all" | "planned" | "in_progress" | "completed" | "cancelled";

interface VisitsTabProps {
  siteId: string;
  // Lets the parent refresh the site header/stepper when field work moves the
  // workflow on.
  onWorkflowChange?: () => void;
  // True once billing has been finalized. A locked site is frozen — the API
  // answers 423 on every visit write — so the controls are hidden rather than
  // left on screen to fail.
  locked?: boolean;
}

const VisitsTab: React.FC<VisitsTabProps> = ({ siteId, onWorkflowChange, locked = false }) => {
  const { user, hasPermission } = useAuth();
  // Viewing visits survives the lock; creating one and everything inside a
  // visit card does not.
  const canViewVisits = hasPermission("IncSite", "visits");
  const canManage = canViewVisits && !locked;
  const canAssign = hasPermission("IncSite", "assign");
  const canExport = hasPermission("IncSite", "export_visits");
  const canDownloadFiles = hasPermission("IncSite", "download_files");
  const canCheckIn = hasPermission("IncSite", "visit_check_in") && !locked;
  const canCheckOut = hasPermission("IncSite", "visit_check_out") && !locked;
  const canVisitMaterial = hasPermission("IncSite", "visit_material") && !locked;
  const canVisitEquipment = hasPermission("IncSite", "visit_equipment") && !locked;
  const canVisitFiles = hasPermission("IncSite", "visit_files") && !locked;
  const canVisitEdit = hasPermission("IncSite", "visit_edit") && !locked;
  const canVisitMembers = hasPermission("IncSite", "visit_members") && !locked;
  const canVisitCancel = hasPermission("IncSite", "visit_cancel") && !locked;
  const canVerifyVisit = hasPermission("IncSite", "visit_verification_approve") && !locked;
  const [visits, setVisits] = useState<any[]>([]);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visitDate, setVisitDate] = useState("");
  const [address, setAddress] = useState("");
  const [creating, setCreating] = useState(false);
  const [showNewVisit, setShowNewVisit] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  // Bumped after creating a visit so the Field Work panel re-reads — creating
  // the first visit auto-starts a field work on the server.
  const [fieldWorkKey, setFieldWorkKey] = useState(0);
  // Set by the Field Work panel when the site's latest field work was cancelled
  // and nothing is open: a visit cannot be created until a new field work is
  // started. The API enforces the same rule (409).
  const [visitBlockedReason, setVisitBlockedReason] = useState<string | null>(null);
  const autoOpened = useRef(false);

  const load = useCallback(async () => {
    try {
      // The portal is the supervisory view, so it asks for the whole site.
      // The API honours scope=all only for this site's supervisor or a holder
      // of IncSite.assign; everyone else still gets their own visits, which is
      // exactly what an engineer's tab should show.
      const res = await api.get(`/inc/sites/${siteId}/visits`, { params: { scope: "all" } });
      setVisits(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  // Beyond one's own visits, adding members needs either the team-assign
  // permission or being this site's supervisor — nothing else grants it — so the
  // tab has to know whether the current user supervises this site.
  const loadSupervisor = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get(`/inc/sites/${siteId}/assignments`);
      const rows: any[] = res.data?.data || [];
      setIsSupervisor(rows.some((r) => Number(r.user_id) === Number(user.id) && r.is_supervisor));
    } catch {
      /* non-fatal */
    }
  }, [siteId, user]);

  useEffect(() => {
    load();
    loadSupervisor();
  }, [load, loadSupervisor]);

  // Visits run in parallel across the team, but one person works one visit at a
  // time — the server rejects a second open visit from the same creator.
  const myOpenVisit = visits.find(
    (v) => Number(v.created_by) === Number(user?.id) && OPEN_STATUSES.includes(v.visit_status),
  );

  // Open the caller's own open visit on first load — that is almost always what
  // they came to the tab for.
  useEffect(() => {
    if (autoOpened.current || loading) return;
    autoOpened.current = true;
    if (myOpenVisit) setOpenIds(new Set([myOpenVisit.id]));
  }, [loading, myOpenVisit]);

  const toggle = (id: number) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const goToVisit = (id: number) => {
    setStatusFilter("all");
    setOnlyMine(false);
    setOpenIds((prev) => new Set(prev).add(id));
    setTimeout(() => document.getElementById(`inc-visit-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const createVisit = async () => {
    setCreating(true);
    try {
      const res = await api.post(`/inc/sites/${siteId}/visits`, { visit_date: visitDate || undefined, address });
      setVisitDate("");
      setAddress("");
      setShowNewVisit(false);
      toast.success("Visit created — check in when you reach the site");
      await load();
      setFieldWorkKey((k) => k + 1);
      onWorkflowChange?.();
      const createdId = res.data?.data?.id;
      if (createdId) goToVisit(Number(createdId));
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setCreating(false);
    }
  };

  // Roll-up shown on the filter chips and mirrored on the workflow stepper.
  const counts = useMemo(
    () =>
      visits.reduce(
        (acc, v) => {
          acc.all += 1;
          if (v.visit_status in acc) (acc as any)[v.visit_status] += 1;
          return acc;
        },
        { all: 0, planned: 0, in_progress: 0, completed: 0, cancelled: 0 } as Record<StatusFilter, number>,
      ),
    [visits],
  );

  const shown = useMemo(
    () =>
      visits.filter(
        (v) =>
          (statusFilter === "all" || v.visit_status === statusFilter) &&
          (!onlyMine || Number(v.created_by) === Number(user?.id)),
      ),
    [visits, statusFilter, onlyMine, user?.id],
  );

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  const FILTERS: Array<{ key: StatusFilter; label: string; tone: string }> = [
    { key: "all", label: "All", tone: "bg-gray-100 text-gray-700" },
    { key: "planned", label: "Planned", tone: "bg-gray-100 text-gray-700" },
    { key: "in_progress", label: "In progress", tone: "bg-blue-100 text-blue-800" },
    { key: "completed", label: "Completed", tone: "bg-green-100 text-green-800" },
    { key: "cancelled", label: "Cancelled", tone: "bg-red-100 text-red-800" },
  ];

  return (
    <div>
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}

      {/* Field work wraps every visit: starting one moves the site to Site
          Accepted, completing it is what moves the site to Work Completed. */}
      <FieldWorkPanel
        siteId={siteId}
        onWorkflowChange={onWorkflowChange}
        locked={locked}
        refreshKey={fieldWorkKey}
        onVisitCreationBlocked={setVisitBlockedReason}
      />

      {/* Visits header: title, new visit, exports */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Site Visits</h3>
          <p className="text-xs text-gray-500">
            Each visit: create it → check in on site → record work, material and photos → check out.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canExport && (
            <button
              onClick={() => downloadIncFile(`/inc/sites/${siteId}/visits/export`, `inc-site-${siteId}-visits.xlsx`)}
              title="Visits, installed material, equipment, claims and documents"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
            >
              <FaFileExcel className="w-4 h-4" /> Export All
            </button>
          )}
          {canDownloadFiles && (
            <button
              onClick={() => downloadIncFile(`/inc/sites/${siteId}/visits/download-files`, `inc-site-${siteId}-visit-files.zip`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
            >
              <FaFileArchive className="w-4 h-4" /> All Visit Files
            </button>
          )}
          {canManage && (
            <button
              onClick={() => setShowNewVisit((s) => !s)}
              disabled={!!myOpenVisit || !!visitBlockedReason}
              title={
                myOpenVisit
                  ? `Finish your visit #${myOpenVisit.visit_number} first`
                  : visitBlockedReason || "Plan a new visit"
              }
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPlus className="w-3 h-3" /> New Visit
            </button>
          )}
        </div>
      </div>

      {/* Your open visit — the one thing that blocks a new one, one click away. */}
      {myOpenVisit && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 mb-3 text-sm text-amber-900">
          <span>
            Your visit <strong>#{myOpenVisit.visit_number}</strong> is {statusLabel(myOpenVisit.visit_status).toLowerCase()}
            {myOpenVisit.check_in_time ? ` (checked in ${timeText(myOpenVisit.check_in_time)})` : " — not checked in yet"}. Finish it before
            starting another.
          </span>
          <button onClick={() => goToVisit(myOpenVisit.id)} className="inline-flex items-center gap-1 text-sm font-medium text-amber-900 hover:underline">
            Go to visit <FaArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {canManage && visitBlockedReason && !myOpenVisit && (
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 mb-3 text-sm text-red-800">
          <FaBan className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{visitBlockedReason}</span>
        </div>
      )}

      {canManage && showNewVisit && !myOpenVisit && !visitBlockedReason && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Planned date</label>
              <input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} className="px-2 py-2 text-sm border border-gray-300 rounded bg-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">Address (optional)</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded bg-white" />
            </div>
            <button
              onClick={createVisit}
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900 disabled:opacity-60"
            >
              <FaPlus className="w-3 h-3" /> {creating ? "Creating..." : "Create Visit"}
            </button>
            <button onClick={() => setShowNewVisit(false)} className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-white">
              Cancel
            </button>
          </div>
          {/* A new visit starts with its creator on it, and nobody else. Saying so
              here is why the member list opens with one name. */}
          <p className="text-xs text-gray-500 mt-2">
            You{user ? ` (${`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username})` : ""} will be the engineer on this
            visit; its supervisor is the site supervisor from the Team tab. If the site has no field work yet, or the last one was
            completed, a new one is started automatically.
          </p>
        </div>
      )}

      {/* Filters */}
      {visits.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTERS.filter((f) => f.key !== "cancelled" || counts.cancelled > 0).map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === f.key ? "border-blue-800 bg-blue-800 text-white" : `border-transparent ${f.tone} hover:border-gray-300`
                }`}
              >
                {f.label}
                <span className={statusFilter === f.key ? "text-blue-100" : "opacity-70"}>{counts[f.key]}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="inline-flex items-center gap-1.5 text-gray-700 cursor-pointer">
              <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} className="rounded border-gray-300 text-blue-800" />
              Only my visits
            </label>
            <button onClick={() => setOpenIds(new Set(shown.map((v) => v.id)))} className="text-blue-700 hover:underline">
              Expand all
            </button>
            <button onClick={() => setOpenIds(new Set())} className="text-blue-700 hover:underline">
              Collapse all
            </button>
          </div>
        </div>
      )}

      {visits.length === 0 ? (
        <div className="text-center border border-dashed border-gray-300 rounded-lg py-10">
          <p className="text-sm text-gray-600 font-medium">No visits yet</p>
          <p className="text-xs text-gray-500 mt-1">
            {canManage ? "Use “New Visit” to plan the first one." : "Visits appear here once engineers create them."}
          </p>
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No visits match this filter.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((v) => {
            // Ownership is a fact about the visit, not a permission — the
            // per-action flags above are what the lock and the permissions gate.
            const isOwner = canViewVisits && Number(v.created_by) === Number(user?.id);
            return (
              <VisitCard
                key={v.id}
                visit={v}
                siteId={siteId}
                isOwner={isOwner}
                currentUserId={user?.id}
                open={openIds.has(v.id)}
                onToggle={() => toggle(v.id)}
                perms={{
                  checkIn: canCheckIn,
                  checkOut: canCheckOut,
                  // Installed material is deliberately NOT owner-only: the
                  // engineer usually records it, but anyone with the permission
                  // may add or correct a line.
                  material: canVisitMaterial,
                  equipment: canVisitEquipment,
                  files: canVisitFiles,
                  // The visit's creator staffs it; above them the site's
                  // supervisor and IncSite.assign holders manage members on every
                  // visit of the site. Mirrors the rule the API enforces.
                  members: canVisitMembers && (isOwner || canAssign || isSupervisor),
                  edit: canVisitEdit,
                  export: canExport,
                  downloadFiles: canDownloadFiles,
                  cancel: canVisitCancel,
                  // Never one's own visit — the card and the API both enforce that.
                  verify: canVerifyVisit,
                }}
                onChange={() => {
                  load();
                  setFieldWorkKey((k) => k + 1);
                  onWorkflowChange?.();
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VisitsTab;
